import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, PillTabs } from "./Shared";
import { TrendingUp } from "lucide-react";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

function Row({ label, value, negative, bold }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className={`text-sm ${bold ? "font-bold text-charcoal" : "text-charcoal/70"}`}>{label}</span>
      <span className={`text-sm font-bold ${negative ? "text-red-500" : "text-charcoal"}`}>{negative ? "-" : ""}{money(value)}</span>
    </div>
  );
}

export default function RevenueTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.get("/app-admin/data/revenue", { params: { period } })
      .then(({ data }) => { if (on) setData(data); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load revenue"))
      .finally(() => on && setLoading(false));
    return () => { on = false; };
  }, [period]);

  return (
    <div className="space-y-5" data-testid="revenue-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold text-charcoal">Dishly revenue</h2>
        <PillTabs options={PERIODS} value={period} onChange={setPeriod} />
      </div>

      {loading || !data ? <p className="py-16 text-center text-sm text-charcoal/40">Loading…</p> : (
        <>
          <div className="rounded-2xl border border-black/5 bg-white p-6 text-center" data-testid="revenue-hero">
            <p className="text-[13px] font-semibold text-charcoal/50">Dishly net revenue</p>
            <p className="mt-2 font-display text-5xl font-bold text-charcoal">{money(data.net_after_processing)}</p>
            <p className="mt-1.5 text-[13px] text-charcoal/55">{data.window_label} · after Stripe processing</p>
          </div>

          <div className="rounded-2xl border border-black/5 bg-white p-5" data-testid="revenue-breakdown">
            <h3 className="mb-1 text-xs font-bold uppercase tracking-widest text-charcoal/40">What Dishly earned</h3>
            <div className="divide-y divide-black/5">
              <Row label="Platform fees (10%)" value={data.platform_fees} />
              <Row label="Customer service fees" value={data.service_fees} />
              <Row label="Item boosts" value={data.boost_revenue} />
              <Row label="Listing capacity" value={data.listing_revenue} />
              {data.credits_revenue > 0 && <Row label="Credits" value={data.credits_revenue} />}
              <Row label="Stripe processing (est.)" value={data.stripe_fees_estimate} negative />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3" data-testid="revenue-metrics">
            {[
              ["Avg order", money(data.avg_order_value)],
              ["Take rate", `${Number(data.take_rate_pct || 0).toFixed(1)}%`],
              ["Completed orders", data.orders_completed],
            ].map(([label, v]) => (
              <div key={label} className="rounded-xl border border-black/5 bg-white p-3.5 text-center">
                <p className="font-display text-xl font-bold text-charcoal">{v}</p>
                <p className="mt-0.5 text-xs font-semibold text-charcoal/55">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-2.5 rounded-2xl border border-black/5 bg-cream/60 p-4" data-testid="revenue-notes">
            <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
            <p className="text-[13px] leading-relaxed text-charcoal/65">
              Customer spend {money(data.gmv)} · tips go straight to creators ({money(data.tips_passthrough)} passed through).
            </p>
          </div>
        </>
      )}
    </div>
  );
}
